import React from 'react';
import { Box, SimpleGrid, Text, Flex, Badge } from '@chakra-ui/react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line
} from 'recharts';

interface AnalyticsData {
  name: string;
  value: number;
  color: string;
}

interface TrendData {
  time: string;
  price: number;
  volume: number;
  marketCap: number;
}

interface AnalyticsDashboardProps {
  portfolioData?: AnalyticsData[];
  sectorData?: AnalyticsData[];
  trendData?: TrendData[];
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  portfolioData = [],
  sectorData = [],
  trendData = []
}) => {
  // Mock data if none provided
  const defaultPortfolioData = portfolioData.length > 0 ? portfolioData : [
    { name: 'DeFi', value: 45, color: '#3182CE' },
    { name: 'Layer 1', value: 30, color: '#38A169' },
    { name: 'Gaming', value: 15, color: '#9F7AEA' },
    { name: 'NFT', value: 10, color: '#F56565' }
  ];

  const defaultSectorData = sectorData.length > 0 ? sectorData : [
    { name: 'Bitcoin', value: 850, color: '#F7931A' },
    { name: 'Ethereum', value: 650, color: '#627EEA' },
    { name: 'Solana', value: 450, color: '#00D4AA' },
    { name: 'Cardano', value: 300, color: '#0033AD' },
    { name: 'Polygon', value: 250, color: '#8247E5' }
  ];

  const defaultTrendData = trendData.length > 0 ? trendData : Array.from({ length: 30 }, (_, i) => ({
    time: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toLocaleDateString(),
    price: 45000 + Math.sin(i / 3) * 5000 + Math.random() * 2000,
    volume: 20 + Math.random() * 15,
    marketCap: 850 + Math.sin(i / 5) * 100 + Math.random() * 50
  }));

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatBillions = (value: number) => {
    return `$${(value).toFixed(1)}B`;
  };

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
        {/* Portfolio Allocation */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
          <Text fontSize="lg" fontWeight="bold" mb={4} color="gray.800">
            Portfolio Allocation
          </Text>
          <Box height="300px">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={defaultPortfolioData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {defaultPortfolioData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [`${value}%`, 'Allocation']}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Flex wrap="wrap" gap={3} mt={4}>
            {defaultPortfolioData.map((item, index) => (
              <Flex key={index} alignItems="center" gap={2}>
                <Box w={3} h={3} bg={item.color} borderRadius="full" />
                <Text fontSize="sm" color="gray.600">
                  {item.name} ({item.value}%)
                </Text>
              </Flex>
            ))}
          </Flex>
        </Box>

        {/* Top Performers */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
          <Text fontSize="lg" fontWeight="bold" mb={4} color="gray.800">
            Top Performers (24h)
          </Text>
          <Box height="300px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={defaultSectorData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="name" 
                  stroke="#718096"
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                />
                <YAxis 
                  stroke="#718096"
                  fontSize={12}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip
                  formatter={(value: number) => [formatCurrency(value), 'Price']}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }}
                />
                <Bar 
                  dataKey="value" 
                  fill="#3182CE"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </SimpleGrid>

      {/* Market Trends */}
      <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Text fontSize="lg" fontWeight="bold" color="gray.800">
            Market Trends (30 Days)
          </Text>
          <Flex gap={4}>
            <Flex alignItems="center" gap={2}>
              <Box w={3} h={3} bg="#3182CE" borderRadius="full" />
              <Text fontSize="sm" color="gray.600">Price</Text>
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Box w={3} h={3} bg="#38A169" borderRadius="full" />
              <Text fontSize="sm" color="gray.600">Market Cap</Text>
            </Flex>
          </Flex>
        </Flex>

        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
          {/* Price Trend */}
          <Box height="250px">
            <Text fontSize="sm" color="gray.600" mb={2}>Average Price Trend</Text>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={defaultTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="time" 
                  stroke="#718096"
                  fontSize={10}
                  tick={false}
                />
                <YAxis 
                  stroke="#718096"
                  fontSize={12}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip
                  formatter={(value: number) => [formatCurrency(value), 'Average Price']}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke="#3182CE"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: '#3182CE' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Market Cap Trend */}
          <Box height="250px">
            <Text fontSize="sm" color="gray.600" mb={2}>Total Market Cap Trend</Text>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={defaultTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="time" 
                  stroke="#718096"
                  fontSize={10}
                  tick={false}
                />
                <YAxis 
                  stroke="#718096"
                  fontSize={12}
                  tickFormatter={(value) => formatBillions(value)}
                />
                <Tooltip
                  formatter={(value: number) => [formatBillions(value), 'Market Cap']}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="marketCap"
                  stroke="#38A169"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: '#38A169' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </SimpleGrid>

        {/* Key Metrics */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mt={6}>
          <Box textAlign="center" p={4} bg="blue.50" borderRadius="md">
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              {formatCurrency(defaultTrendData[defaultTrendData.length - 1]?.price || 0)}
            </Text>
            <Text fontSize="sm" color="gray.600">Current Avg Price</Text>
          </Box>
          <Box textAlign="center" p={4} bg="green.50" borderRadius="md">
            <Text fontSize="2xl" fontWeight="bold" color="green.600">
              {formatBillions(defaultTrendData[defaultTrendData.length - 1]?.marketCap || 0)}
            </Text>
            <Text fontSize="sm" color="gray.600">Total Market Cap</Text>
          </Box>
          <Box textAlign="center" p={4} bg="purple.50" borderRadius="md">
            <Text fontSize="2xl" fontWeight="bold" color="purple.600">
              {(defaultTrendData[defaultTrendData.length - 1]?.volume || 0).toFixed(1)}B
            </Text>
            <Text fontSize="sm" color="gray.600">24h Volume</Text>
          </Box>
          <Box textAlign="center" p={4} bg="orange.50" borderRadius="md">
            <Text fontSize="2xl" fontWeight="bold" color="orange.600">
              <Badge colorScheme="green">+12.5%</Badge>
            </Text>
            <Text fontSize="sm" color="gray.600">24h Change</Text>
          </Box>
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default AnalyticsDashboard;